import type {
  Appointment,
  Encounter,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { NOTE_LABEL_TAG_SYSTEM } from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import type { AssessmentSearchQuery } from '#src/models/assessments/assessment.ts'
import {
  CompositionNote,
  hasUnfinishedAiSummaryAttempt,
  isAiSummaryRetryDue,
  isAiSummaryStale,
  nextAiSummaryFailedAttempts,
} from '#src/models/compositionNote.ts'
import { CompositionCategory } from '#src/models/compositions/constants.ts'
import type { NoteLabel } from '#src/models/noteLabel.ts'
import {
  andQueryFilters,
  assessmentKindCategoryFilter,
  noteLabelCategoryFilter,
  resolveAssessmentKindToken,
  resolveDisciplineToken,
} from '#src/models/visitNoteCategory.ts'
import { resolveId } from '#src/references.ts'
import { dateRangeFilter, type QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class ParticipantNote extends CompositionNote {
  constructor({
    participant,
    encounter,
    ...rest
  }: ConstructorParameters<typeof CompositionNote>[0] & {
    participant: Reference<Patient>
    encounter?: Reference<Encounter>
  }) {
    super(rest)
    this.participant = participant
    this.encounter = encounter
  }

  get participant(): Reference<Patient> {
    invariant(
      isOfType(this.resource.subject, 'Patient'),
      `Subject for ${this.reference.reference} must be a Patient`
    )
    return this.resource.subject
  }

  set participant(value: Reference<Patient>) {
    this.resource.subject = value
  }

  get isSigned(): boolean {
    return !!this.resource.attester?.length
  }

  get signedOn(): string | undefined {
    return this.resource.attester?.[0]?.time
  }

  get signerReference(): Reference<Practitioner> | undefined {
    return this.resource.attester?.[0]?.party as
      | Reference<Practitioner>
      | undefined
  }

  get signerDisplay(): string | undefined {
    return this.signerReference?.display
  }

  /* FHIR's Narrative.div is XHTML by spec, but note body sections store a
   * JSON-serialized Tiptap doc here. */
  get contentSectionDiv(): string | undefined {
    return this.resource.section?.[0]?.text?.div
  }

  get aiSummaryIsStale(): boolean {
    return isAiSummaryStale({
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.dateString,
    })
  }

  get aiSummaryAttemptUnfinished(): boolean {
    return hasUnfinishedAiSummaryAttempt({
      startedAt: this.aiSummaryStartedAt,
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.dateString,
    })
  }

  aiSummaryRetryDue(now?: number): boolean {
    return isAiSummaryRetryDue({
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.dateString,
      failedAttempts: this.aiSummaryFailedAttempts,
      now,
    })
  }

  get nextAiSummaryFailedAttempts(): number {
    return nextAiSummaryFailedAttempts({
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.dateString,
      failedAttempts: this.aiSummaryFailedAttempts,
    })
  }

  /* Liveblocks addressing for note bodies, in one place because it is derived
   * on both sides of the wire: the browser's editor writes under these keys and
   * the MCP read path looks under them. A reader that computes either key
   * differently finds nothing, and finding nothing is indistinguishable from a
   * note nobody has opened — so it returns a well-formed stale note instead of
   * an error.
   *
   * Both formats are load-bearing: they are what production rooms hold today,
   * and changing either orphans every existing fragment.
   *
   * One room per PARTICIPANT, never one per note. The note is a fragment inside
   * it, which is why reading a note starts with a Medplum lookup — a note id
   * alone does not name a room. */
  static roomIdForParticipant(participantId: string): string {
    return `Patient/${participantId}`
  }

  /* Each note's fragment is named for its Composition id — the `field` the note
   * editor hands the Liveblocks TipTap extension. */
  static fragmentNameForNote(noteId: string): string {
    return noteId
  }

  /* Callers holding only a participant id — the browser mounts its room
   * provider per participant, with no note yet — want the static instead. */
  get roomId(): string {
    const participantId = resolveId(this.participant)
    /* Thunked: `this.reference` is only worth building when this fails. */
    invariant(
      participantId,
      () => `Participant for ${this.reference.reference} must have an id`
    )
    return ParticipantNote.roomIdForParticipant(participantId)
  }

  get fragmentName(): string {
    return ParticipantNote.fragmentNameForNote(this.id)
  }

  // Note: This will technically return notes with a non-patient subject, but 'subject:Patient': '*' didn't seem to work.
  static queryAll({
    labels,
  }: {
    labels?: NoteLabel['reference'][]
  } = {}): QueryTypes {
    return {
      category: CompositionCategory.Note,
      'status:not': 'entered-in-error',
      'subject:missing': false,
      _sort: '-_lastUpdated',
      _count: '1000',
      ...(labels &&
        labels.length > 0 && {
          _tag: labels.map((l) => ({
            system: NOTE_LABEL_TAG_SYSTEM,
            code: l.reference,
          })),
        }),
    }
  }

  static queryByParticipant(
    participant: Reference<Patient>,
    { labels }: { labels?: NoteLabel['reference'][] } = {}
  ): QueryTypes {
    return {
      ...this.queryAll({ labels }),
      subject: participant,
    }
  }

  /* Just enough of specific notes to decide whether each can be signed: the
   * attester that says whether it already is, and the subject the sign endpoint
   * checks the request against.
   *
   * Trimmed with `_elements` because the caller is a bulk-sign affordance keyed
   * on a canvas selection — a drag can select dozens of notes at once, and every
   * body it would otherwise pull is a whole serialized editor document for a
   * decision that reads two fields. Ids come from nodes the viewer can already
   * see, and the result is only ever read for those two fields, never rendered.
   *
   * No category or status clause on purpose: the caller names ids it resolved
   * from desk nodes, and a node pointing at something that is not a participant
   * note has to come back so it can be excluded rather than silently missing. */
  static querySignatureByIds(ids: readonly string[]): QueryTypes {
    return {
      ...this.queryByIds(ids),
      _elements: 'attester,subject',
    }
  }

  /* The signed participant notes that name a resource in a section entry.
   *
   * On `ParticipantNote` rather than `ExtendedComposition` so it inherits the
   * note category: `section.entry` is shared with assessments, care plan
   * snapshots and anything else a clinician writes into, and an intervention's
   * activity feed lists notes. The generic `ExtendedComposition.byEntry` stays
   * deliberately category-free for the validated-tool parent lookups, which do
   * want every kind.
   *
   * Signature is `attester`, not `status`: an Orbit-native note stays
   * `preliminary` after it is signed, so `status=final` would return nothing and
   * a status-only filter would return every draft. `attester:missing` is the
   * predicate the drafts surfaces use, read the other way round.
   *
   * Scoped by participant for the reason the validated-tool parent lookups are:
   * content copied between charts carries its references along, so another
   * participant's record can name this resource. */
  static querySignedByEntry(
    entryReference: string,
    participant: Reference<Patient>,
    /* How many the caller intends to SHOW. The query asks for one more, so a
     * caller can tell a full page from an exactly-full result and say which it
     * is — a list that silently stops at its limit reads as "that is all of
     * them". Omit to inherit the ambient page size. */
    displayLimit?: number
  ): QueryTypes {
    const query: QueryTypes = {
      ...this.queryByParticipant(participant),
      entry: entryReference,
      'attester:missing': false,
    }
    if (displayLimit !== undefined) {
      query._count = String(displayLimit + 1)
    }
    return query
  }

  static byAppointment(appointment: Reference<Appointment>): QueryTypes {
    return {
      ...this.queryAll(),
      'encounter:Encounter.appointment': appointment,
    }
  }

  static assessmentsTabSearchQuery({
    practitioner,
    subject,
    ids,
    kinds,
    disciplines,
    dateFrom,
    dateTo,
    signed,
  }: AssessmentSearchQuery = {}): QueryTypes {
    const base: QueryTypes = subject
      ? this.queryByParticipant(subject)
      : this.queryAll()
    if (!subject && ids?.length) {
      base._id = ids.join(',')
    }

    const query: QueryTypes = { ...base }
    if (subject && ids?.length) {
      query._id = ids.join(',')
    }
    if (practitioner) {
      query.author = practitioner.reference
    }
    Object.assign(
      query,
      andQueryFilters(
        assessmentKindCategoryFilter(),
        noteLabelCategoryFilter((kinds ?? []).map(resolveAssessmentKindToken)),
        noteLabelCategoryFilter(
          (disciplines ?? []).map(resolveDisciplineToken)
        ),
        dateRangeFilter('period', { from: dateFrom, to: dateTo })
      )
    )
    if (signed !== undefined) {
      query['attester:missing'] = signed ? false : true
    }
    query._sort = '-period'
    return query
  }
}
